import { IMarketFormValues } from './marketFormValues';
import { z } from 'zod';
import { baseModelSchema } from '../../baseModel';

/**
 * Zod schema for validating market data.
 * Extends the base model schema with market-specific fields.
 *
 * @property {string} code - Unique identifier code (max 5 characters)
 * @property {string} name - Name of the market (max 255 characters)
 * @property {string} channel - Market channel type ('L' for Local, 'E' for Export, 'R' for Retail)
 * @property {number} [regionId] - Optional ID of the associated region
 */
export const marketSchema = baseModelSchema.extend({
    code: z.string().nonempty('Code is required').max(5, 'Code can only be up to 5 characters long.').default(''),
    name: z.string().nonempty('Name is required').max(255, 'Name can only be up to 255 characters long.').default(''),
    channel: z.enum(['L', 'E', 'R']).default('L'),
    regionId: z.number().optional(),
});

/**
 * TypeScript interface representing a Market object.
 * Generated from the Zod schema for type safety.
 */
export type IMarket = z.infer<typeof marketSchema>;

/**
 * Creates a validated Market object from form values.
 *
 * @param {IMarketFormValues} formValues - The form data to convert
 * @returns {IMarket} A validated market object
 * @throws {ZodError} If validation fails
 */
export function Market(formValues : IMarketFormValues) : IMarket {
    const { region, channel } = formValues;
    return marketSchema.parse({
        ...formValues,
        channel: channel ? channel.value : undefined,
        regionId: region?.value ? Number(region.value) : undefined,
    });
};
